import type { HeightmapAsset, ParamValues } from '../types';
import { spanToMillimetres } from '../params';
import { bilinear, clamp01, mod, remap } from '../math/scalar';
import { rotatePoint } from './helpers';
import { definePattern } from './registry';

/**
 * Bitmap height source — the "drop your own image" path.
 *
 * The fit modes are the whole point of this pattern. `wall` stretches one copy
 * of the image over the entire grid, so a single picture becomes a relief that
 * every panel holds one tile of; `span` repeats it every N panels; `panel` puts
 * a copy on each panel. Mapping happens in wall space, so neighbouring panels
 * sample adjacent parts of the same bitmap and the relief lines up at the joint
 * by construction.
 */

type FitMode = 'wall' | 'cover' | 'span' | 'panel';
type TileMode = 'repeat' | 'mirror' | 'clamp';

interface HeightmapParams extends ParamValues {
  asset: string;
  fit: FitMode;
  spanX: number;
  spanY: number;
  angle: number;
  offsetX: number;
  offsetY: number;
  tile: TileMode;
  blackPoint: number;
  whitePoint: number;
  invert: boolean;
  smooth: boolean;
}

/** Applies the tiling rule to a normalised coordinate. */
function wrap(value: number, mode: TileMode): number {
  switch (mode) {
    case 'mirror': {
      const t = mod(value, 2);
      return t > 1 ? 2 - t : t;
    }
    case 'clamp':
      return clamp01(value);
    case 'repeat':
    default:
      return mod(value, 1);
  }
}

/**
 * Samples a bitmap at normalised coordinates with `v = 0` at the bottom, which
 * matches wall space rather than image space.
 */
function sampleAsset(asset: HeightmapAsset, u: number, v: number, smooth: boolean): number {
  const { width, height, data } = asset;
  const px = u * (width - 1);
  // Row 0 of the bitmap is the top of the image, so flip v on the way in.
  const py = (1 - v) * (height - 1);

  if (!smooth) {
    const x = Math.min(width - 1, Math.max(0, Math.round(px)));
    const y = Math.min(height - 1, Math.max(0, Math.round(py)));
    return data[y * width + x];
  }

  const x0 = Math.min(width - 1, Math.max(0, Math.floor(px)));
  const y0 = Math.min(height - 1, Math.max(0, Math.floor(py)));
  const x1 = Math.min(width - 1, x0 + 1);
  const y1 = Math.min(height - 1, y0 + 1);
  const tx = px - x0;
  const ty = py - y0;

  return bilinear(
    data[y0 * width + x0],
    data[y0 * width + x1],
    data[y1 * width + x0],
    data[y1 * width + x1],
    tx,
    ty,
  );
}

export const heightmapPattern = definePattern<HeightmapParams, HeightmapAsset | null>({
  id: 'heightmap',
  name: 'Image',
  description:
    'Turns a grayscale image into relief. Bright pixels come forward, dark pixels stay at the base.',
  category: 'image',
  tags: ['import', 'bitmap', 'custom'],
  params: [
    { kind: 'asset', key: 'asset', label: 'Image', default: '', group: 'Source' },
    {
      kind: 'select',
      key: 'fit',
      label: 'Fit',
      options: [
        { value: 'wall', label: 'Stretch across the whole wall' },
        { value: 'cover', label: 'Cover the wall, keep aspect ratio' },
        { value: 'span', label: 'Repeat every N panels' },
        { value: 'panel', label: 'One copy per panel' },
      ],
      default: 'wall',
      help: 'Wall and span fits let one image run across several panels.',
      group: 'Placement',
    },
    {
      kind: 'span',
      key: 'spanX',
      label: 'Span X',
      min: 0.25,
      max: 12,
      step: 0.25,
      default: 2,
      group: 'Placement',
    },
    {
      kind: 'span',
      key: 'spanY',
      label: 'Span Y',
      min: 0.25,
      max: 12,
      step: 0.25,
      default: 2,
      group: 'Placement',
    },
    { kind: 'angle', key: 'angle', label: 'Rotation', default: 0, group: 'Placement' },
    {
      kind: 'number',
      key: 'offsetX',
      label: 'Offset X',
      min: -1,
      max: 1,
      step: 0.01,
      default: 0,
      group: 'Placement',
    },
    {
      kind: 'number',
      key: 'offsetY',
      label: 'Offset Y',
      min: -1,
      max: 1,
      step: 0.01,
      default: 0,
      group: 'Placement',
    },
    {
      kind: 'select',
      key: 'tile',
      label: 'Tiling',
      options: [
        { value: 'mirror', label: 'Mirror — always seamless' },
        { value: 'repeat', label: 'Repeat' },
        { value: 'clamp', label: 'Clamp to the edge' },
      ],
      default: 'mirror',
      help: 'Mirroring makes any image tile without a visible seam.',
      group: 'Placement',
    },
    {
      kind: 'number',
      key: 'blackPoint',
      label: 'Black point',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0,
      group: 'Levels',
    },
    {
      kind: 'number',
      key: 'whitePoint',
      label: 'White point',
      min: 0,
      max: 1,
      step: 0.01,
      default: 1,
      group: 'Levels',
    },
    { kind: 'boolean', key: 'invert', label: 'Invert', default: false, group: 'Levels' },
    {
      kind: 'boolean',
      key: 'smooth',
      label: 'Smooth sampling',
      default: true,
      help: 'Bilinear filtering. Turn it off for pixel-art reliefs.',
      group: 'Levels',
    },
  ],
  prepare(params, context) {
    return context.assets[params.asset] ?? null;
  },
  sample(sample, params, asset) {
    if (!asset) return 0;

    const { wall } = sample;
    let u: number;
    let v: number;

    if (params.fit === 'panel') {
      u = sample.u;
      v = sample.v;
    } else {
      const centred = rotatePoint(
        sample.x - wall.width * 0.5,
        sample.y - wall.height * 0.5,
        params.angle,
      );
      const x = centred.x + wall.width * 0.5;
      const y = centred.y + wall.height * 0.5;

      if (params.fit === 'span') {
        u = x / spanToMillimetres(params.spanX, wall.pitchX);
        v = y / spanToMillimetres(params.spanY, wall.pitchY);
      } else if (params.fit === 'cover') {
        // Scale so the shorter image axis fills the wall, then centre the rest.
        const wallAspect = wall.width / Math.max(wall.height, 1e-3);
        const imageAspect = asset.width / Math.max(asset.height, 1);
        if (imageAspect > wallAspect) {
          const scale = wallAspect / imageAspect;
          u = (x / wall.width - 0.5) * scale + 0.5;
          v = y / wall.height;
        } else {
          const scale = imageAspect / wallAspect;
          u = x / wall.width;
          v = (y / wall.height - 0.5) * scale + 0.5;
        }
      } else {
        u = x / wall.width;
        v = y / wall.height;
      }
    }

    const value = sampleAsset(
      asset,
      wrap(u + params.offsetX, params.tile),
      wrap(v + params.offsetY, params.tile),
      params.smooth,
    );

    const low = Math.min(params.blackPoint, params.whitePoint - 1e-3);
    const levelled = clamp01(remap(value, low, params.whitePoint, 0, 1));
    return params.invert ? 1 - levelled : levelled;
  },
});
